import SectionHeading from "@/components/SectionHeading";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import CTASection from "@/components/CTASection";
import { images } from "@/data/business";
import { Sparkles, HeartHandshake, ShieldCheck, GraduationCap, Users, Award, BadgeCheck } from "lucide-react";

const VALUES = [
  { icon: HeartHandshake, title: "Kindness", body: "Every child is met with warmth and patience — a positive experience is our first outcome." },
  { icon: ShieldCheck, title: "Safety", body: "Clinical safety and infection control are non-negotiable, on every single visit." },
  { icon: GraduationCap, title: "Education", body: "We build lifelong habits, not just clean teeth. Curiosity is welcomed." },
  { icon: Users, title: "Partnership", body: "We work with schools, parents and clinicians as one team around each child." },
];

const About = () => {
  if (typeof document !== "undefined") document.title = "About Us — School Smiles Australia";
  return (
    <>
      <section className="container-page pt-16 md:pt-24 pb-8" data-testid="about-hero">
        <div className="max-w-3xl">
          <span className="eyebrow"><Sparkles size={14} strokeWidth={2.5}/> About us</span>
          <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#14324A]">
            A gentler kind of dental care, brought to your school
          </h1>
          <p className="mt-5 text-[#4B6B86] text-lg leading-relaxed">
            We started School Smiles Australia because access to preventive dental care shouldn't depend on how far a family can travel. Our mobile clinic goes to schools so children can be seen in a place they already feel safe.
          </p>
        </div>
      </section>

      <section className="container-page grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch py-10" data-testid="about-story">
        <ImagePlaceholder src={images.friendlyDentist} alt="Our team caring for a young patient" ratio="aspect-[4/3]" testId="about-story-image" />
        <div className="card-soft p-10 flex flex-col justify-center">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-[#14324A]">Our story</h2>
          <p className="mt-4 text-[#4B6B86] leading-relaxed">
            School Smiles Australia was founded by a small team of dental professionals who saw how many families were struggling to access routine check-ups. We designed a mobile clinic that brings the same clinical standards you would expect in a fixed dental practice — but with the warmth of a school community.
          </p>
          <p className="mt-4 text-[#4B6B86] leading-relaxed">
            Today, we partner with primary and secondary schools across Australia to deliver child-friendly preventive care, with clear communication with parents at every step.
          </p>
        </div>
      </section>

      <section className="container-page py-16 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="about-mission">
        <div className="card-soft p-10">
          <Award size={26} className="text-[#1677C8]" strokeWidth={2.5}/>
          <h2 className="mt-3 font-display font-bold text-2xl text-[#14324A]">Our mission</h2>
          <p className="mt-3 text-[#4B6B86] leading-relaxed">
            To make preventive dental care effortless for Australian schools and families, and to help every child grow up with a healthy, confident smile.
          </p>
        </div>
        <div className="card-soft p-10 bg-[#F4F9FF]">
          <HeartHandshake size={26} className="text-[#23B7C5]" strokeWidth={2.5}/>
          <h2 className="mt-3 font-display font-bold text-2xl text-[#14324A]">Our approach</h2>
          <p className="mt-3 text-[#4B6B86] leading-relaxed">
            Child-first, consent-based, evidence-informed. We take the time to explain what we're doing, invite questions, and never rush a nervous patient.
          </p>
        </div>
      </section>

      <section className="container-page py-16" data-testid="about-values">
        <SectionHeading eyebrow="Our values" title="What guides every visit" />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v) => (
            <div key={v.title} className="card-soft p-8" data-testid={`about-value-${v.title.toLowerCase()}`}>
              <div className="w-12 h-12 rounded-xl bg-[#F4F9FF] text-[#1677C8] inline-flex items-center justify-center">
                <v.icon size={22} strokeWidth={2.5}/>
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg text-[#14324A]">{v.title}</h3>
              <p className="mt-2 text-sm text-[#4B6B86] leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page py-16" data-testid="about-team">
        <SectionHeading eyebrow="Our team" title="Qualified, caring professionals" description="Team profiles will be added here. Names, roles and photos are placeholders and will be replaced with real staff details before launch." />
        <div className="mt-10 max-w-3xl mx-auto">
          {[
            {
              name: "Muhammad Umar",
              role: "Principal Practitioner",
              ahpraRegistered: true,
              bio: "Muhammad Umar graduated from the University of Melbourne and is passionate about helping children develop healthy smiles that last a lifetime. He is committed to providing gentle, compassionate dental care in a friendly and supportive environment, helping young patients feel comfortable and confident during every visit. With a strong focus on prevention and education, Umar works closely with families and schools to promote lifelong oral health while making high-quality dental care accessible through School Smiles Australia's mobile dental service.",
            },
          ].map(({ name, role, bio, ahpraRegistered }) => (
            <div key={role} className="card-soft p-8 md:p-10 flex flex-col md:flex-row md:items-start gap-6 md:gap-8" data-testid={`about-team-${role.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="mx-auto md:mx-0 w-28 h-28 rounded-full bg-gradient-to-br from-[#1677C8] to-[#23B7C5] shrink-0" aria-hidden="true"/>
              <div className="flex-1 text-center md:text-left">
                <div className="font-display font-semibold text-xl md:text-2xl text-[#14324A]">{name}</div>
                <div className="text-sm text-[#1677C8] font-medium">{role}</div>
                {ahpraRegistered && (
                  <span
                    className="mt-3 inline-flex items-center gap-1.5 bg-[#FFD45C] text-[#14324A] text-xs font-semibold px-3 py-1.5 rounded-full"
                    data-testid="ahpra-pill"
                    aria-label="AHPRA registered dental practitioner"
                    title="AHPRA registered dental practitioner"
                  >
                    <BadgeCheck size={14} strokeWidth={2.5}/>
                    AHPRA Registered
                  </span>
                )}
                <p className="mt-4 text-[#4B6B86] leading-relaxed">{bio}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page py-8" data-testid="about-credentials">
        <div className="card-soft p-10">
          <h2 className="font-display font-bold text-2xl text-[#14324A]">Qualifications, registrations and insurance</h2>
          <p className="mt-3 text-[#4B6B86] leading-relaxed">
            Our clinicians hold current Australian dental qualifications and registration. Full professional details, registration numbers and current insurance information will be listed here in consultation with our compliance advisors before launch. This section is a placeholder — no registration or provider numbers should be assumed until published here.
          </p>
        </div>
      </section>

      <div className="container-page"><CTASection testIdPrefix="about-cta" /></div>
    </>
  );
};

export default About;
